import React from 'react';
import { flowRight } from 'lodash/fp';
import { withSnackbar } from 'notistack';

import StepsForm from 'ui/form/StepsForm';
import { getCountryRegions } from 'utils/county-data';

import LoaderStep from './steps/LoaderStep';
import CompanyDetailsStep from './steps/CompanyDetailsStep';
import CompanyAccountStep from './steps/CompanyAccountStep';
import { loadCompany, validateAccount } from './services';
import * as valid from 'utils/validators';
import { withAppContext } from '../../AppContext';
import { isCustomerType, MAX_SMALL_INTEGER, NAME_MAX_LENGTH } from './consts';
import { addCompany } from '../../Services';
import { minAllowedValue, notZeroOrNegative } from '../../../utils/validators';

const LOADER_STEP = 'loader';
const DETAIL_STEP = 'detail';
const ACCOUNT_STEP = 'account';

const INITIAL_VALUES = {
    business_name: '',
    address1: '',
    address2: '',
    city: '',
    country: 'US',
    region: 'AL',
    postal_code: '',
    name: '',
    logo: '',
    type: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    license_type: 'billable_this_month',
    subscription_level: 'advanced',
    seats: '',
    licenseAck: false,
    createAppAccount: true,
    createPartnerAccount: false,
};

const validateAccountField = fieldName => async (values, errors) => {
    let result = null;
    if (!errors[fieldName]) {
        const exists = await validateAccount({
            [fieldName]: values[fieldName],
        });
        if (exists) {
            result = [fieldName, 'exists'];
        }
    }
    return result;
};

const isNotCustomer = values => !isCustomerType(values.type);

const findRegion = (regions, region) =>
    regions && !regions[region] ? Object.keys(regions)[0] : region;

const STEPS = {
    [LOADER_STEP]: {
        component: LoaderStep,
        update: async ({ goNext, setInitValues, extra }) => {
            const data = await loadCompany(extra.params.id);
            const country = data.country;
            const regions = getCountryRegions(country);
            const region = findRegion(regions, data.region);
            setInitValues({ ...INITIAL_VALUES, country, region });
            goNext();
        },
        next: DETAIL_STEP,
    },
    [DETAIL_STEP]: {
        label: 'Company Details',
        component: CompanyDetailsStep,
        validate: valid.rules([
            valid.notEmpty('type'),
            valid.notEmpty('business_name'),
            valid.maxLength('business_name', NAME_MAX_LENGTH),
            valid.notEmpty('name'),
            valid.maxLength('name', NAME_MAX_LENGTH),
            valid.conditionCheck(
                isNotCustomer,
                valid.rules([
                    valid.notEmpty('address1'),
                    valid.notEmpty('city'),
                    valid.notEmpty('country'),
                    valid.notEmpty('region'),
                    valid.notEmpty('postal_code'),
                ])
            ),
        ]),
        next: ACCOUNT_STEP,
        back: ({ redirectTo }) => redirectTo('/companies'),
    },
    [ACCOUNT_STEP]: {
        label: 'Create account',
        component: CompanyAccountStep,
        validate: valid.rules([
            valid.notEmpty('first_name'),
            valid.notEmpty('last_name'),
            valid.notEmpty('email'),
            valid.emailType('email'),
            validateAccountField('email'),
            valid.notEmpty('license_type'),
            valid.notEmpty('subscription_level'),
            valid.maxAllowedValue('seats', MAX_SMALL_INTEGER),
            valid.notZeroOrNegative('seats'),
            valid.notEmpty('seats'),
            valid.notEmpty('licenseAck'),
            valid.conditionCheck(
                isNotCustomer,
                valid.rules([
                    valid.notEmpty('username'),
                    validateAccountField('username'),
                ])
            ),
        ]),
        next: async ({ onSave, values, redirectTo }) => {
            const url = await onSave(values);
            redirectTo(url);
        },
        back: DETAIL_STEP,
    },
};

const handleSave = (notify, app) => async data => {
    const uid = await addCompany(app.currentUser.uuid, data);
    notify('Organization created');
    return `/companies/${uid}`;
};

let CreateCompany = ({ app, enqueueSnackbar, match: { params } }) => {
    return (
        <StepsForm
            steps={STEPS}
            first={LOADER_STEP}
            onSave={handleSave(enqueueSnackbar, app)}
            extra={{
                user: app.currentUser,
                creating: true,
                params,
            }}
        />
    );
};

export default flowRight(withAppContext, withSnackbar)(CreateCompany);
